import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Critical path - loaded eagerly (needed on first paint)
import AuthCallback from "@/components/AuthCallback";
import Landing from "@/components/Landing";
import Navbar from "@/components/Navbar";
import SupportButton from "@/components/SupportButton";

// Lazy-loaded pages (code-split into separate chunks)
const Generator = React.lazy(() => import("@/components/Generator"));
const History = React.lazy(() => import("@/components/History"));
const Profile = React.lazy(() => import("@/components/Profile"));
const AdminDashboard = React.lazy(() => import("@/components/AdminDashboard"));
const SeoPage = React.lazy(() => import("@/components/SeoPage"));
const RolePage = React.lazy(() => import("@/components/RolePage"));
const BlogPost = React.lazy(() => import("@/components/BlogPost"));
const ResumeExamplesIndex = React.lazy(() => import("@/components/ResumeExamplesIndex"));
const BlogIndex = React.lazy(() => import("@/components/BlogIndex"));
const JobTracker = React.lazy(() => import("@/components/JobTracker"));
const AtsChecker = React.lazy(() => import("@/components/AtsChecker"));
const ColdEmailGenerator = React.lazy(() => import("@/components/ColdEmailGenerator"));
const BulkGenerate = React.lazy(() => import("@/components/BulkGenerate"));
const NotFound = React.lazy(() => import("@/components/NotFound"));

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-[#0a0a0f]">
      <div className="w-7 h-7 border-2 border-[#c8a96e] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/generate" replace /> : <Landing />;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback - detect session_id in URL hash synchronously
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <Navbar />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/generate" element={<Generator />} />
          <Route path="/history" element={<History />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/admin" element={<AdminDashboard />} />
          {/* SEO Pages */}
          <Route path="/ai-resume-builder" element={<SeoPage />} />
          <Route path="/ats-resume-builder" element={<SeoPage />} />
          <Route path="/resume-builder-for-freshers" element={<SeoPage />} />
          <Route path="/free-resume-builder-india" element={<SeoPage />} />
          <Route path="/ai-cover-letter-generator" element={<SeoPage />} />
          <Route path="/linkedin-resume-builder" element={<SeoPage />} />
          <Route path="/resume-builder-for-experienced" element={<SeoPage />} />
          <Route path="/interview-prep-ai" element={<SeoPage />} />
          {/* Resume Examples */}
          <Route path="/resume-examples" element={<ResumeExamplesIndex />} />
          <Route path="/resume-examples/:role" element={<RolePage />} />
          {/* Blog */}
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          {/* Tools & Career */}
          <Route path="/tools/ats-score-checker" element={<AtsChecker />} />
          <Route path="/job-tracker" element={<JobTracker />} />
          <Route path="/career/cold-email" element={<ColdEmailGenerator />} />
          <Route path="/career/bulk-generate" element={<BulkGenerate />} />
          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <SupportButton />
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: '#13131a',
                  border: '1px solid #2a2a38',
                  color: '#f0ede8',
                  fontFamily: 'DM Sans, sans-serif',
                },
              }}
            />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
